import React, { useState } from 'react';
import TimeAgo from 'react-timeago';
import NewConversationModal from './NewConversationModal';

const ConversationList = ({ 
  conversations, 
  selectedConversation, 
  onConversationSelect, 
  onNewConversation,
  currentUser,
  loading 
}) => {
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);

  const getConversationTitle = (conversation) => {
    if (conversation.title && conversation.title.trim()) {
      return conversation.title;
    }
    
    // Generate title from usernames (excluding current user)
    const otherUsers = conversation.users?.filter(u => u && u.username && u.username !== currentUser.username) || [];
    if (otherUsers.length === 0) return 'Conversa';
    if (otherUsers.length === 1) return otherUsers[0].username;
    if (otherUsers.length === 2) return `${otherUsers[0].username}, ${otherUsers[1].username}`;
    return `${otherUsers[0].username} e mais ${otherUsers.length - 1}`;
  };

  const getConversationAvatar = (conversation) => {
    const otherUsers = conversation.users?.filter(u => u && u.username && u.username !== currentUser.username) || [];
    if (otherUsers.length === 1 && otherUsers[0].avatar) {
      return otherUsers[0].avatar;
    }
    return null; // Will show default group avatar
  };

  const getLastMessagePreview = (conversation) => {
    if (!conversation.latest_message) return 'Nenhuma mensagem';
    
    const message = conversation.latest_message;
    const isOwnMessage = message.user?.username === currentUser.username;
    const prefix = isOwnMessage ? 'Você: ' : `${message.user?.username || 'Alguém'}: `;
    
    if (message.post) {
      return `${prefix}compartilhou um post`;
    }
    
    if (message.is_command) {
      return `${prefix}${message.body}`;
    }
    
    const body = message.body || '';
    const maxLength = 50;
    const truncated = body.length > maxLength ? `${body.substring(0, maxLength)}...` : body;
    
    return `${prefix}${truncated}`;
  };

  const handleNewConversation = async (usernames, title) => {
    const result = await onNewConversation(usernames, title);
    if (result) {
      setShowNewConversationModal(false);
    }
  };

  if (loading) {
    return (
      <div className="conversation-list">
        <div className="loading-conversations">
          <p>Carregando conversas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="conversation-list">
      <div className="new-conversation-button-container">
        <button 
          className="new-conversation-button"
          onClick={() => setShowNewConversationModal(true)}
        >
          + Nova Conversa
        </button>
      </div>
      
      <div className="conversations-scroll">
        {conversations.length === 0 ? (
          <div className="no-conversations">
            <p>Nenhuma conversa encontrada</p>
            <p>Clique em "Nova Conversa" para começar</p>
          </div>
        ) : (
          conversations.map(conversation => {
            const isSelected = selectedConversation?._id?.$oid === conversation._id.$oid;
            const avatar = getConversationAvatar(conversation);
            const hasUnread = conversation.has_unread || conversation.unreadMessage;
            
            return (
              <div
                key={conversation._id.$oid}
                className={`conversation-item ${isSelected ? 'selected' : ''} ${hasUnread ? 'unread' : ''}`}
                onClick={() => onConversationSelect(conversation)}
              >
                <div className="conversation-avatar">
                  {avatar ? (
                    <img src={avatar} alt="Avatar" />
                  ) : (
                    <div className="default-avatar">
                      {conversation.users?.length > 2 ? '👥' : '👤'}
                    </div>
                  )}
                  {hasUnread && <div className="unread-indicator"></div>}
                </div>
                
                <div className="conversation-info">
                  <div className="conversation-header">
                    <h4 className="conversation-title">
                      {getConversationTitle(conversation)}
                    </h4>
                    {conversation.latest_message && (
                      <span className="conversation-time">
                        <TimeAgo date={conversation.latest_message.created_at} />
                      </span>
                    )}
                  </div>
                  
                  <p className="conversation-preview">
                    {getLastMessagePreview(conversation)}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {showNewConversationModal && (
        <NewConversationModal
          onClose={() => setShowNewConversationModal(false)}
          onCreateConversation={handleNewConversation}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

export default ConversationList;
